import { EventBus } from '@pp/toolkit';

import { UserSession } from '../../config/UserSession';
import { Logger } from '../../logger/Logger';

import type { FeatureServiceFetcher } from './fetchers/types';

export enum FeatureServiceState {
  IDLE,
  FETCHING,
  READY,
}

export class FeatureServiceBase extends EventBus<{
  ready: () => void;
  updated: () => void;
}> {
  private fetcher: FeatureServiceFetcher | undefined;
  private _features: Record<string, boolean | undefined> | null | undefined;
  private _state: FeatureServiceState = FeatureServiceState.IDLE;

  set state(state: FeatureServiceState) {
    this._state = state;
    if (this._state === FeatureServiceState.READY) {
      this.dispatchEvent('ready');
    }
  }

  get state(): FeatureServiceState {
    return this._state;
  }

  get features(): Record<string, boolean | undefined> | null | undefined {
    return this._features;
  }

  set features(features: Record<string, boolean | undefined> | null | undefined) {
    this._features = features;
    this.dispatchEvent('updated');
  }

  init(fetcher: FeatureServiceFetcher): void {
    if (this.state === FeatureServiceState.IDLE) {
      this.fetcher = fetcher;
      this.fetch();
    }
  }

  fetch(): void {
    if (!this.fetcher) {
      return;
    }
    this.state = FeatureServiceState.FETCHING;
    this.fetcher
      .fetch()
      .then((payload) => {
        this.state = FeatureServiceState.READY;
        this.features = Object.assign(payload, this.getFromSession());
      })
      .catch(() => {
        // make a sad face
        this.state = FeatureServiceState.READY;
        Logger.logFailure('featureFlags', {
          place: 'FeatureService.fetch',
          additional: 'failedToFetch',
        });
      });
  }

  getFromSession(): Record<string, boolean> {
    const fromSession: Record<string, boolean> = {};
    const session = UserSession.getSession();

    for (const key in session) {
      if (key.startsWith('feature-')) {
        const feature = key.replace('feature-', '');
        fromSession[feature] = session[key] === 'true';
      }
    }

    return fromSession;
  }

  /**
   * Gets the feature by name
   *
   * Returns undefined if the service is not ready
   *
   * Returns null if the feature is not found
   *
   * @param name - the name of the feature
   * @returns the feature value or null if not found
   * */
  getFeature(name: string): boolean | null | undefined {
    if (this.state !== FeatureServiceState.READY) {
      return undefined;
    }
    return this.features?.[name] ?? null;
  }
}

export const FeatureService = new FeatureServiceBase();
